import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, ClockIcon, ChevronLeftIcon } from 'lucide-react';
import TherapistCard from '../components/TherapistCard';
const BookingPage = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTherapist, setSelectedTherapist] = useState<number | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const navigate = useNavigate();
  // Generate dates for the next 7 days
  const dateOptions: Date[] = Array.from({
    length: 7
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return date;
  });
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Reset time slot when date changes
  };
  const handleTherapistSelect = (id: number) => {
    setSelectedTherapist(id);
    setSelectedTimeSlot(null); // Reset time slot when therapist changes
  };
  const handleTimeSlotSelect = (time: string) => {
    setSelectedTimeSlot(time);
  };
  const handleContinue = () => {
    if (selectedTherapist && selectedTimeSlot) {
      navigate('/checkout');
    }
  };
  return <div className="container mx-auto px-4 py-8" data-id="element-50">
      <div className="max-w-4xl mx-auto" data-id="element-51">
        <div className="flex items-center mb-6" data-id="element-52">
          <button onClick={() => navigate('/services')} className="text-gray-600 hover:text-teal-600 flex items-center" data-id="element-53">
            <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-54" />
            Back to Services
          </button>
        </div>
        <h1 className="text-3xl font-bold mb-8" data-id="element-55">Book Your Appointment</h1>
        {/* Selected Service Summary */}
        <div className="bg-teal-50 p-4 rounded-lg mb-8" data-id="element-56">
          <h2 className="text-lg font-semibold" data-id="element-57">Gel Manicure</h2>
          <p className="text-gray-600" data-id="element-58">60 mins • £45</p>
        </div>
        {/* Date Selection */}
        <div className="mb-8" data-id="element-59">
          <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-60">
            <CalendarIcon className="h-5 w-5 mr-2 text-teal-600" data-id="element-61" />
            Select a Date
          </h2>
          <div className="flex overflow-x-auto pb-2 space-x-2" data-id="element-62">
            {dateOptions.map((date, index) => <button key={index} onClick={() => handleDateSelect(date)} className={`flex flex-col items-center justify-center p-3 min-w-[80px] rounded-lg ${selectedDate.toDateString() === date.toDateString() ? 'bg-teal-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-63">
                <span className="text-xs font-medium" data-id="element-64">
                  {date.toLocaleDateString('en-US', {
                weekday: 'short'
              })}
                </span>
                <span className="text-lg font-bold" data-id="element-65">{date.getDate()}</span>
                <span className="text-xs" data-id="element-66">
                  {date.toLocaleDateString('en-US', {
                month: 'short'
              })}
                </span>
              </button>)}
          </div>
        </div>
        {/* Therapist Selection */}
        <div className="mb-8" data-id="element-67">
          <h2 className="text-xl font-semibold mb-4" data-id="element-68">Select a Therapist</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-69">
            {therapists.map(therapist => <TherapistCard key={therapist.id} therapist={therapist} isSelected={selectedTherapist === therapist.id} onSelect={() => handleTherapistSelect(therapist.id)} data-id="element-70" />)}
          </div>
        </div>
        {/* Time Slot Selection */}
        {selectedTherapist && <div className="mb-8" data-id="element-71">
            <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-72">
              <ClockIcon className="h-5 w-5 mr-2 text-teal-600" data-id="element-73" />
              Select a Time
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-id="element-74">
              {timeSlots.map(time => <button key={time} onClick={() => handleTimeSlotSelect(time)} className={`py-2 px-4 rounded-lg text-center ${selectedTimeSlot === time ? 'bg-teal-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-75">
                  {time}
                </button>)}
            </div>
          </div>}
        {/* Continue Button */}
        <div className="mt-8 flex justify-end" data-id="element-76">
          <button onClick={handleContinue} disabled={!selectedTherapist || !selectedTimeSlot} className={`px-6 py-3 rounded-md ${selectedTherapist && selectedTimeSlot ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-77">
            Continue to Checkout
          </button>
        </div>
      </div>
    </div>;
};
// Sample data
const therapists = [{
  id: 1,
  name: 'Emma Wilson',
  rating: 4.9,
  reviewCount: 124,
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
  specialties: ['Nails', 'Facial']
}, {
  id: 2,
  name: 'Sophie Chen',
  rating: 4.8,
  reviewCount: 98,
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
  specialties: ['Nails', 'Massage']
}, {
  id: 3,
  name: 'Olivia Taylor',
  rating: 4.7,
  reviewCount: 86,
  image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80',
  specialties: ['Nails', 'Hair']
}];
const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'];
export default BookingPage;